import { Search, Sparkles, X, Zap } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useSearchBox } from "react-instantsearch"
import { SearchPostHogClient } from "../../search-standalone/services/SearchPostHogClient"
import { Suggestion } from "../../types/search"
import { cn } from "../../utils/cn"
import { useSearchClient } from "./SearchProvider"

interface EnhancedSearchBoxProps {
	className?: string
	placeholder?: string
	showSearchTypeToggle?: boolean
	availableModes?: SearchType[]
	defaultMode?: SearchType
	onSearchModeChange?: (mode: SearchType) => void
}

/**
 * NOTE: Search type toggle is disabled by default.
 * Each collection uses its configured default search method (keyword/semantic/hybrid).
 * The toggle is kept for potential future use but should remain disabled in production.
 */

export type SearchType = "keyword" | "semantic" | "hybrid"

/**
 * EnhancedSearchBox combines InstantSearch with WebSocket autocomplete
 * Features:
 * - InstantSearch integration for main search
 * - WebSocket autocomplete suggestions
 * - Search type toggle (keyword/semantic/hybrid)
 * - Keyboard navigation
 */
export const EnhancedSearchBox = ({
	className,
	placeholder = "Search products...",
	showSearchTypeToggle = false, // Disabled: Collections use their configured default method
	availableModes = ["keyword", "semantic", "hybrid"],
	defaultMode = "hybrid",
	onSearchModeChange,
}: EnhancedSearchBoxProps) => {
	const { query, refine } = useSearchBox()
	const client = useSearchClient()
	const searchPostHog = SearchPostHogClient.getInstance()
	const [localQuery, setLocalQuery] = useState(query)
	const [searchType, setSearchType] = useState<SearchType>(defaultMode)
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [isOpen, setIsOpen] = useState(false)
	const [selectedIndex, setSelectedIndex] = useState(-1)
	const [hasNLCollection, setHasNLCollection] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	// Sync local query with InstantSearch query
	useEffect(() => {
		setLocalQuery(query)
	}, [query])

	// Detect if any selected collection uses natural language search
	useEffect(() => {
		const nlEnabled = client.selectedCollections.some((col) => col.defaultSearchMethod === "natural_language")
		setHasNLCollection(nlEnabled)
		console.log("[EnhancedSearchBox] NL Detection:", nlEnabled, client.selectedCollections)
	}, [client.selectedCollections])

	// Disable suggestions for now - they're causing issues with the InstantSearch adapter
	// TODO: Implement suggestions using direct Typesense API or WebSocket
	useEffect(() => {
		// Suggestions disabled - just clear them
		setSuggestions([])
		setIsOpen(false)
	}, [localQuery])

	// Click outside to close
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setLocalQuery(value)

		// Only auto-search for non-NL collections
		// NL collections require Enter key or Search button click
		if (!hasNLCollection) {
			refine(value)
		}
	}

	const captureSearchSubmitted = (trigger: "enter" | "button" | "suggestion", submittedQuery: string) => {
		searchPostHog.capture(
			"search_submitted",
			client.buildAnalyticsProperties({
				...searchPostHog.buildQueryProperties(submittedQuery),
				search_mode: hasNLCollection ? "natural_language" : searchType,
				trigger,
				collection_count: client.selectedCollections.length,
				collection_names: client.selectedCollections.map((collection) => collection.name),
			}),
			client.analyticsContext.externalSessionId,
		)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowDown") {
			e.preventDefault()
			setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
		} else if (e.key === "ArrowUp") {
			e.preventDefault()
			setSelectedIndex((prev) => Math.max(prev - 1, -1))
		} else if (e.key === "Enter") {
			e.preventDefault()
			if (selectedIndex >= 0 && suggestions[selectedIndex]) {
				handleSuggestionClick(suggestions[selectedIndex])
			} else {
				// Trigger search on Enter (important for NL collections)
				captureSearchSubmitted("enter", localQuery)
				refine(localQuery)
				setIsOpen(false)
			}
		} else if (e.key === "Escape") {
			setIsOpen(false)
			inputRef.current?.blur()
		}
	}

	const handleSearchClick = () => {
		captureSearchSubmitted("button", localQuery)
		refine(localQuery)
		setIsOpen(false)
	}

	const handleSuggestionClick = (suggestion: Suggestion) => {
		captureSearchSubmitted("suggestion", suggestion.text)
		setLocalQuery(suggestion.text)
		refine(suggestion.text)
		setIsOpen(false)
	}

	const clearQuery = () => {
		searchPostHog.capture(
			"search_query_cleared",
			client.buildAnalyticsProperties({
				...searchPostHog.buildQueryProperties(localQuery),
				search_mode: hasNLCollection ? "natural_language" : searchType,
				collection_count: client.selectedCollections.length,
				collection_names: client.selectedCollections.map((collection) => collection.name),
			}),
			client.analyticsContext.externalSessionId,
		)
		setLocalQuery("")
		refine("")
		inputRef.current?.focus()
	}

	const getSearchTypeIcon = () => {
		switch (searchType) {
			case "semantic":
				return <Sparkles className="w-4 h-4" />
			case "hybrid":
				return <Zap className="w-4 h-4" />
			default:
				return <Search className="w-4 h-4" />
		}
	}

	const getSearchTypeLabel = () => {
		switch (searchType) {
			case "semantic":
				return "AI Search"
			case "hybrid":
				return "Smart Search"
			default:
				return "Keyword"
		}
	}

	const cycleSearchType = () => {
		const currentIndex = availableModes.indexOf(searchType)
		const nextIndex = (currentIndex + 1) % availableModes.length
		const newMode = availableModes[nextIndex]
		searchPostHog.capture(
			"search_mode_toggled",
			client.buildAnalyticsProperties({
				previous_search_mode: searchType,
				search_mode: newMode,
				available_modes: availableModes,
			}),
			client.analyticsContext.externalSessionId,
		)
		setSearchType(newMode)
		onSearchModeChange?.(newMode)
	}

	return (
		<div className={cn("relative w-full z-10", className)} ref={containerRef}>
			<div
				className={cn(
					"relative flex items-center rounded-md border border-input bg-background/50 shadow-sm transition-all focus-within:ring-1 focus-within:ring-ring",
					isOpen && suggestions.length > 0 && "rounded-b-none border-b-0",
				)}>
				{/* AI Search Indicator */}
				{hasNLCollection && (
					<div className="absolute left-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-900/30 pointer-events-none">
						<Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
						<span className="text-xs font-medium text-purple-700 dark:text-purple-300 hidden sm:inline">
							AI Search
						</span>
					</div>
				)}

				{/* Search Type Toggle */}
				{showSearchTypeToggle && !hasNLCollection && (
					<button
						className="absolute left-3 flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-accent transition-colors group"
						onClick={cycleSearchType}
						title={`Current: ${getSearchTypeLabel()}. Click to change.`}>
						{getSearchTypeIcon()}
						<span className="text-xs font-medium hidden sm:inline">{getSearchTypeLabel()}</span>
					</button>
				)}

				{/* Search Input */}
				<input
					className={cn(
						"flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
						hasNLCollection ? "pl-24 sm:pl-28 pr-32" : showSearchTypeToggle ? "pl-28 sm:pl-32 pr-10" : "pl-10 pr-10",
					)}
					onChange={handleInputChange}
					onFocus={() => localQuery && suggestions.length > 0 && setIsOpen(true)}
					onKeyDown={handleKeyDown}
					placeholder={
						hasNLCollection ? "Ask in natural language (e.g., 'elegant white dresses under 3000')..." : placeholder
					}
					ref={inputRef}
					type="text"
					value={localQuery}
				/>

				{/* Search Button (for NL) and Clear Button */}
				<div className="absolute right-3 flex items-center gap-2">
					{hasNLCollection && localQuery && (
						<button
							className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
							onClick={handleSearchClick}
							title="Search with Natural Language">
							<Sparkles className="w-3.5 h-3.5" />
							<span className="hidden sm:inline">Search</span>
						</button>
					)}
					{localQuery && (
						<button
							className="flex h-full items-center justify-center text-muted-foreground hover:text-foreground"
							onClick={clearQuery}
							title="Clear search">
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>

			{/* Suggestions Dropdown */}
			{isOpen && suggestions.length > 0 && (
				<div className="absolute w-full rounded-b-md border border-t-0 bg-popover shadow-md overflow-hidden">
					<ul className="py-1">
						{suggestions.map((suggestion, index) => (
							<li key={suggestion.id || `${suggestion.type}-${suggestion.text}-${index}`}>
								<button
									className={cn(
										"w-full px-4 py-2 flex items-center gap-3 text-sm transition-colors text-left hover:bg-accent hover:text-accent-foreground",
										selectedIndex === index && "bg-accent text-accent-foreground",
									)}
									onClick={() => handleSuggestionClick(suggestion)}>
									{suggestion.product ? (
										<>
											{suggestion.product.image ? (
												<img
													alt=""
													className="w-8 h-8 rounded object-cover"
													src={suggestion.product.image}
												/>
											) : (
												<div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
													<Search className="w-4 h-4 text-muted-foreground" />
												</div>
											)}
											<div className="flex-1 min-w-0">
												<p className="font-medium truncate">{suggestion.product.name}</p>
												<p className="text-xs text-muted-foreground">
													{suggestion.product.category} · ${suggestion.product.price}
												</p>
											</div>
										</>
									) : (
										<>
											<span className="flex items-center justify-center w-6 h-6 rounded bg-muted/50">
												<Search className="w-4 h-4 text-muted-foreground" />
											</span>
											<div className="flex-1">
												<p>{suggestion.text}</p>
												<p className="text-xs text-muted-foreground capitalize">{suggestion.type}</p>
											</div>
										</>
									)}
								</button>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
